import { useEffect, useState, type FormEvent } from 'react';
import { db, withSyncMeta } from '@/offline/db';
import { enqueueSync } from '@/offline/sync';
import { useAuthStore } from '@/lib/auth-store';
import { minScopeForProcedure, scopeBlockReason } from '@/lib/scope';
import { useProtocolStore } from '@/modules/protocol/protocol-store';
import { CareMatrix } from '../CareMatrix';
import { ContactCockpit } from '../ContactCockpit';
import type { EPCR, Procedure } from '@/types';
import { Radio } from 'lucide-react';
import { DemoFillButton } from '@/components/DemoFillButton';
import { demoProcedureRows } from '@/lib/demo-data';
import { cn } from '@/lib/cn';
import {
  Plus, Syringe, Clock, Check, X as XIcon,
  Shield, AlertTriangle, BookOpen,
} from 'lucide-react';

interface Props {
  epcr: EPCR;
  /**
   * Optional ePCR-level patch handler. Threaded through so the
   * embedded CareMatrix at the top of the tab can write
   * assessment / disposition / set_findings patches back to the
   * record. When omitted, the CareMatrix is not rendered.
   */
  onUpdate?: (updates: Partial<EPCR>) => void;
}

// Master NEMSIS-coded procedure list (used in deviation mode only)
const PROCEDURE_CATALOG = [
  'IV Access', 'IO Access', 'BVM Ventilation', 'Supraglottic Airway',
  'Endotracheal Intubation', '12-Lead Acquisition', 'Cardiac Monitoring',
  'Defibrillation', 'Synchronized Cardioversion', 'Transcutaneous Pacing',
  'Splinting', 'Hemorrhage Control - Tourniquet', 'Hemorrhage Control - Pressure',
  'Spinal Motion Restriction', 'Wound Care', 'Glucometry',
  'Pulse Oximetry', 'Capnography', 'CPAP', 'Nebulizer Treatment',
  'NTG Administration', 'Patient Restraint', 'Needle Decompression',
  'Nasopharyngeal Airway', 'Oropharyngeal Airway', 'Suctioning',
  'CPR', 'Eye Irrigation', 'Childbirth Delivery',
];

const BODY_SITES = [
  'Left Arm', 'Right Arm', 'Left Hand', 'Right Hand',
  'Left Leg', 'Right Leg', 'Left Foot', 'Right Foot',
  'Left Anterior Chest', 'Right Anterior Chest',
  'Abdomen', 'Back', 'Head', 'Neck', 'Other',
];

export function ProceduresTab({ epcr, onUpdate }: Props) {
  const [cockpitOpen, setCockpitOpen] = useState(false);
  const { user } = useAuthStore();
  const { permittedProcedures, activeProtocols, completeStep, addDeviation } = useProtocolStore();
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [deviationMode, setDeviationMode] = useState(false);
  const [form, setForm] = useState(defaultForm());

  useEffect(() => {
    loadProcedures();
  }, [epcr.id]);

  async function loadProcedures() {
    const records = await db.procedures.where('epcr_id').equals(epcr.id).sortBy('timestamp');
    setProcedures(records);
  }

  // Build list of permitted procedures from active protocols
  const permitted = permittedProcedures();
  const hasActiveProtocols = activeProtocols().length > 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.procedure_type || !user) return;

    // Outcome must be explicitly chosen (Finding 19 — no silent
    // first-pass-success inflation). Form-level guard so submit
    // never proceeds with an empty outcome.
    if (!form.outcome) {
      alert('Outcome is required — choose successful, unsuccessful, or not attempted.');
      return;
    }

    // Determine if this procedure is protocol-backed.
    //
    // Semantic clarification: a "deviation" is only meaningful when an
    // active protocol exists. If no protocol is active, the procedure
    // is a free clinical entry — not a deviation — and no justification
    // is required. The previous logic forced isDeviation=true for any
    // procedure when permitted=[] (no active protocols), which then
    // failed validation because the justification field never rendered
    // (it was gated on hasActiveProtocols). User-visible bug:
    // "the form blocks me but won't let me add justification."
    const match = permitted.find((p) => p.procedureType === form.procedure_type);
    const isDeviation = hasActiveProtocols && (deviationMode || !match);

    if (isDeviation && !form.deviation_reason.trim()) {
      alert('Deviation requires a clinical justification.');
      return;
    }

    // Hard scope block — Sprint K. EMT cannot insert paramedic-only
    // procedures even with a deviation reason. RI Protocol §1.01.
    const required = minScopeForProcedure(form.procedure_type);
    const scope = scopeBlockReason(user.cert_level, required);
    if (scope) {
      alert(`${form.procedure_type}: ${scope}`);
      return;
    }

    const proc: Procedure = {
      id: crypto.randomUUID(),
      epcr_id: epcr.id,
      timestamp: new Date(form.timestamp).toISOString(),
      procedure_type: form.procedure_type,
      body_site: form.body_site || null,
      performed_by: user.id,
      attempt_number: Number(form.attempt_number),
      outcome: form.outcome as Procedure['outcome'],
      complications: form.complications || null,
      protocol_id: match?.protocolId ?? null,
      protocol_step_order: match?.stepOrder ?? null,
      is_deviation: isDeviation,
      deviation_reason: isDeviation ? form.deviation_reason : null,
      created_at: new Date().toISOString(),
    };

    await db.procedures.add(withSyncMeta(proc));
    await enqueueSync('procedures', proc.id, 'insert', proc as unknown as Record<string, unknown>);

    // Link to protocol step (if it matched)
    if (match && proc.outcome === 'successful') {
      await completeStep({
        protocolId: match.protocolId,
        stepOrder: match.stepOrder,
        linkedProcedureId: proc.id,
      });
    }

    // Record deviation
    if (isDeviation) {
      await addDeviation({
        protocolId: match?.protocolId ?? activeProtocols()[0]?.id ?? 'unknown',
        stepOrder: match?.stepOrder ?? null,
        reason: form.deviation_reason,
        actionType: 'procedure',
        actionId: proc.id,
      });
    }

    setForm(defaultForm());
    setDeviationMode(false);
    setShowForm(false);
    loadProcedures();
  }

  const selectedIsPermitted = permitted.some((p) => p.procedureType === form.procedure_type);
  const selectedMatch = permitted.find((p) => p.procedureType === form.procedure_type);

  // Cockpit mode — when active, the cockpit replaces the rest of the
  // tab content (Add Procedure form + history list). The workflow
  // rail OUTSIDE this tab stays accessible because the rail is
  // rendered at the WorkflowBody level, not inside this tab.
  if (cockpitOpen && onUpdate) {
    return (
      <ContactCockpit
        epcr={epcr}
        onUpdate={onUpdate}
        onClose={() => setCockpitOpen(false)}
      />
    );
  }

  return (
    <div className="space-y-4">
      {/* Cockpit launcher — opens the protocol-driven reporting
          console (parallel to Code 99 Mode). Replaces the Procedures
          tab content with a three-zone workspace: live clocks,
          quick-tap intervention palette, running event log. The
          workflow rail on the left stays accessible the whole time
          so the EMT can flip to Patient / Vitals / Disposition
          mid-call. */}
      {onUpdate && !epcr.locked && (
        <button
          onClick={() => setCockpitOpen(true)}
          className="rail-button w-full flex items-center justify-center gap-2 px-3 py-3 text-sm font-bold uppercase tracking-widest"
        >
          <Radio size={16} />
          Open Cockpit
        </button>
      )}

      {/* Protocol-Driven Care Matrix — appears at the top when an
          active protocol matches the chief complaint / impression. */}
      {onUpdate && <CareMatrix epcr={epcr} onUpdate={onUpdate} />}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-lg font-semibold text-slate-200">
          Procedures ({procedures.length})
        </h3>
        <div className="flex items-center gap-2">
          <DemoFillButton
            label="Fill procedures demo"
            onFill={async () => {
              if (!user) return;
              const rows = demoProcedureRows(epcr.id, user.id);
              for (const row of rows) {
                await db.procedures.add(withSyncMeta(row));
                await enqueueSync('procedures', row.id, 'insert', row as unknown as Record<string, unknown>);
              }
              await loadProcedures();
            }}
          />
          {!epcr.locked && (
            <button onClick={() => setShowForm(!showForm)} className="btn-primary gap-1">
              <Plus size={16} /> Add Procedure
            </button>
          )}
        </div>
      </div>

      {/* Protocol context banner */}
      {hasActiveProtocols && !showForm && (
        <div className="flex items-center gap-2 rounded-lg border border-brand-500/30 bg-brand-500/5 px-3 py-2 text-xs text-brand-300">
          <BookOpen size={14} />
          {permitted.length} procedure{permitted.length !== 1 ? 's' : ''} available from active protocol{activeProtocols().length !== 1 ? 's' : ''}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="rounded-lg border border-brand-500/30 bg-slate-800 p-4 space-y-4">
          {/* Deviation toggle */}
          {hasActiveProtocols && (
            <div className="flex items-center gap-3 rounded-lg border border-slate-700 bg-slate-900 p-3">
              <Shield size={16} className={deviationMode ? 'text-ems-amber' : 'text-brand-400'} />
              <div className="flex-1 text-xs">
                <div className="font-medium text-slate-200">
                  {deviationMode ? 'Deviation Mode' : 'Protocol Mode'}
                </div>
                <div className="text-slate-400">
                  {deviationMode
                    ? 'Any procedure can be documented. Clinical justification required.'
                    : `Only protocol-permitted procedures (${permitted.length}) are shown.`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setDeviationMode(!deviationMode);
                  setForm((f) => ({ ...f, procedure_type: '' }));
                }}
                className={cn(
                  'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
                  deviationMode
                    ? 'border-ems-amber text-ems-amber'
                    : 'border-slate-600 text-slate-400',
                )}
              >
                {deviationMode ? 'Back to Protocol' : 'Deviate'}
              </button>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-300">Procedure</label>
              <select
                value={form.procedure_type}
                onChange={(e) => setForm((f) => ({ ...f, procedure_type: e.target.value }))}
                className="input-field"
                required
              >
                <option value="">Select procedure...</option>
                {!hasActiveProtocols || deviationMode
                  ? PROCEDURE_CATALOG.map((p) => <option key={p} value={p}>{p}</option>)
                  : permitted.map((p) => (
                      <option key={`${p.protocolId}-${p.stepOrder}`} value={p.procedureType}>
                        {p.procedureType}
                      </option>
                    ))}
              </select>
              {selectedMatch && (
                <p className="mt-1 flex items-center gap-1 text-xs text-brand-300">
                  <BookOpen size={12} />
                  Protocol step {selectedMatch.stepOrder} · {
                    activeProtocols().find((p) => p.id === selectedMatch.protocolId)?.name
                  }
                </p>
              )}
              {hasActiveProtocols && deviationMode && !selectedIsPermitted && form.procedure_type && (
                <p className="mt-1 flex items-center gap-1 text-xs text-ems-amber">
                  <AlertTriangle size={12} />
                  Outside active protocol — deviation will be logged
                </p>
              )}
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-slate-300">Body Site</label>
              <select
                value={form.body_site}
                onChange={(e) => setForm((f) => ({ ...f, body_site: e.target.value }))}
                className="input-field"
              >
                <option value="">N/A</option>
                {BODY_SITES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-300">Time</label>
              <div className="flex gap-1">
                <input
                  type="datetime-local"
                  value={form.timestamp}
                  onChange={(e) => setForm((f) => ({ ...f, timestamp: e.target.value }))}
                  className="input-field flex-1"
                />
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, timestamp: new Date().toISOString().slice(0, 16) }))}
                  className="px-2 border border-slate-600 bg-slate-800/80 text-[10px] font-bold uppercase tracking-wider text-slate-300 hover:bg-slate-700"
                  title="Set to current time"
                >
                  Now
                </button>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-300">Attempt #</label>
              <input
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                min="1"
                value={form.attempt_number}
                onChange={(e) => setForm((f) => ({ ...f, attempt_number: e.target.value }))}
                className="input-field"
              />
            </div>
            <div>
              <label className={cn(
                'mb-1 block text-sm font-medium',
                form.outcome ? 'text-slate-300' : 'text-ems-amber',
              )}>
                Outcome {form.outcome ? '' : '(required)'}
              </label>
              <div className="flex gap-2">
                {(['successful', 'unsuccessful', 'not_attempted'] as const).map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, outcome: o }))}
                    className={cn(
                      'rounded-lg px-3 py-2 text-sm font-medium capitalize min-h-[2.5rem] transition-colors',
                      form.outcome === o
                        ? o === 'successful' ? 'bg-ems-green text-white'
                          : o === 'unsuccessful' ? 'bg-ems-red text-white'
                          : 'bg-slate-600 text-white'
                        : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                    )}
                  >
                    {o.replace('_', ' ')}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-slate-300">Complications</label>
              <input
                type="text"
                value={form.complications}
                onChange={(e) => setForm((f) => ({ ...f, complications: e.target.value }))}
                className="input-field"
                placeholder="None, or describe..."
              />
            </div>

            {/* Deviation reason — visible whenever the submit-time
                isDeviation would be true. Mirrors the handleSubmit
                logic: only when active protocols exist AND either the
                user toggled Deviate mode OR the chosen procedure isn't
                on the permitted list. With no active protocols, any
                procedure is a free entry (no justification required). */}
            {hasActiveProtocols && (deviationMode || (form.procedure_type && !selectedIsPermitted)) && (
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-ems-amber">
                  Clinical Justification for Deviation (required)
                </label>
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {[
                    'Protocol step inappropriate for this presentation',
                    'Patient refused protocol-indicated step',
                    'Above-scope alternative used (justified)',
                    'Equipment unavailable',
                    'Online medical control directed alternative',
                  ].map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      onClick={() => setForm((f) => ({
                        ...f,
                        deviation_reason: f.deviation_reason
                          ? `${f.deviation_reason}\n${preset}`
                          : preset,
                      }))}
                      className="rounded border border-amber-700/60 bg-amber-950/40 px-2 py-1 text-[11px] font-medium text-amber-200 hover:bg-amber-900/60"
                    >
                      + {preset}
                    </button>
                  ))}
                </div>
                <textarea
                  value={form.deviation_reason}
                  onChange={(e) => setForm((f) => ({ ...f, deviation_reason: e.target.value }))}
                  className="input-field min-h-[5rem] resize-y"
                  placeholder="e.g., Protocol called for IV but patient had no accessible veins — IO placed instead"
                  required
                />
                <p className="mt-1 text-[11px] text-slate-400">
                  This text is captured in the audit log and the narrative.
                  Tap a preset above to seed the field, then refine.
                </p>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button type="submit" className="btn-primary">Save Procedure</button>
            <button type="button" onClick={() => { setShowForm(false); setDeviationMode(false); }} className="btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      )}

      {procedures.length > 0 ? (
        <div className="space-y-2">
          {procedures.map((proc) => (
            <div key={proc.id} className="flex items-center gap-3 rounded-lg bg-slate-800 px-4 py-3">
              <div className={cn(
                'flex h-8 w-8 items-center justify-center rounded-full',
                proc.outcome === 'successful' ? 'bg-green-900 text-ems-green' :
                proc.outcome === 'unsuccessful' ? 'bg-red-900 text-ems-red' :
                'bg-slate-700 text-slate-400'
              )}>
                {proc.outcome === 'successful' ? <Check size={16} /> :
                 proc.outcome === 'unsuccessful' ? <XIcon size={16} /> :
                 <Syringe size={16} />}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-200">{proc.procedure_type}</span>
                  {proc.is_deviation && (
                    <span className="flex items-center gap-1 rounded bg-amber-900 px-1.5 py-0.5 text-xs text-amber-300">
                      <AlertTriangle size={10} /> DEVIATION
                    </span>
                  )}
                  {proc.protocol_id && !proc.is_deviation && (
                    <span className="flex items-center gap-1 rounded bg-brand-500/20 px-1.5 py-0.5 text-xs text-brand-300">
                      <BookOpen size={10} /> Step {proc.protocol_step_order}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400 flex-wrap">
                  <Clock size={12} />
                  {new Date(proc.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  {proc.body_site && <span>| {proc.body_site}</span>}
                  <span>| Attempt {proc.attempt_number}</span>
                  {proc.complications && <span className="text-ems-amber">| {proc.complications}</span>}
                </div>
                {proc.is_deviation && proc.deviation_reason && (
                  <p className="mt-1 text-xs italic text-amber-400">
                    Rationale: {proc.deviation_reason}
                  </p>
                )}
              </div>
              <span className={cn(
                'rounded px-2 py-0.5 text-xs font-medium capitalize',
                proc.outcome === 'successful' ? 'bg-green-900 text-green-300' :
                proc.outcome === 'unsuccessful' ? 'bg-red-900 text-red-300' :
                'bg-slate-700 text-slate-400'
              )}>
                {proc.outcome.replace('_', ' ')}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-slate-700 bg-slate-900 py-8 text-center">
          <Syringe size={32} className="mx-auto mb-2 text-slate-600" />
          <p className="text-slate-500">No procedures documented</p>
        </div>
      )}
    </div>
  );
}

function defaultForm() {
  return {
    procedure_type: '',
    body_site: '',
    timestamp: new Date().toISOString().slice(0, 16),
    attempt_number: '1',
    // Empty by default — Finding 19 (security audit) flagged that
    // pre-selecting 'successful' was silently inflating first-pass-
    // success metrics. Outcome must be an explicit choice; handleSubmit
    // rejects an empty value, and the Outcome row in the form pops
    // an amber border until selected.
    outcome: '' as string,
    complications: '',
    deviation_reason: '',
  };
}
